import {
  getAppConfiguration,
  setAppConfiguration,
} from "lib/app_configuration";
import BaseController from "confinus_ui/base_controller";
import { addScriptTag } from "lib/util/add_script_tag";

// Controller for drawing and editing polygons on Google Maps for serviceability regions
export default class PolygonEditorController extends BaseController {
  public static targets = ["mapContainer", "geoJsonInput"];

  public static values = {
    apiKey: String,
    zoom: { type: Number, default: 10 },
    center: { type: String, default: null },
    initialPolygon: { type: String, default: null },
  };

  declare private readonly mapContainerTarget: HTMLDivElement;
  declare private readonly geoJsonInputTarget: HTMLInputElement;
  declare private readonly apiKeyValue: string;
  declare private readonly zoomValue: number;
  declare private readonly centerValue: string | null;
  declare private readonly initialPolygonValue: string | null;

  private googleMap!: google.maps.Map | null;
  private drawingManager!: google.maps.drawing.DrawingManager | null;
  private currentPolygon!: google.maps.Polygon | null;

  public initialize() {
    super.initialize();
    console.log("PolygonEditor: Controller initializing");
    this.attachMapScript();
  }

  public connect() {
    super.connect();
    console.log("PolygonEditor: Controller connected");
    this.onMapReady(() => {
      console.log("PolygonEditor: Google Maps API ready");
      this.createMap();
      this.initializeDrawingManager();
      this.loadInitialPolygon();
    });
  }

  public disconnect() {
    this.cleanup();
    super.disconnect();
  }

  private attachMapScript() {
    if (document.getElementById("google-maps-script-tag")) {
      return;
    }
    window.__mapInitialised = this.mapInitialised.bind(this);
    const key = this.apiKeyValue;
    // Include drawing library for Drawing Manager
    const src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=drawing&callback=__mapInitialised`;
    addScriptTag(src, {
      async: true,
      defer: true,
      id: "google-maps-script-tag",
    });
  }

  private mapInitialised() {
    setAppConfiguration("mapHasLoaded", true);
    const evt = new CustomEvent("google-maps-script:ready", {
      bubbles: true,
      cancelable: false,
    });
    document.dispatchEvent(evt);
  }

  private onMapReady(callback: () => void) {
    if (getAppConfiguration("mapHasLoaded")) {
      return callback();
    }
    const readyListener = () => {
      callback();
      document.removeEventListener("google-maps-script:ready", readyListener);
    };
    document.addEventListener("google-maps-script:ready", readyListener);
  }

  private createMap() {
    this.googleMap = new google.maps.Map(this.mapContainerTarget, {
      center: this.mapCenter,
      zoom: this.zoomValue,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
    });
  }

  private initializeDrawingManager() {
    if (!this.googleMap) {
      return;
    }

    this.drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: null, // Start with no drawing mode active
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [google.maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
        editable: true,
        draggable: false,
      },
    });

    this.drawingManager.setMap(this.googleMap);

    // Listen for polygon completion
    google.maps.event.addListener(
      this.drawingManager,
      "overlaycomplete",
      (event: google.maps.drawing.OverlayCompleteEvent) => {
        if (event.type === google.maps.drawing.OverlayType.POLYGON) {
          this.handlePolygonComplete(event.overlay as google.maps.Polygon);
        }
      },
    );
  }

  private handlePolygonComplete(polygon: google.maps.Polygon) {
    console.log("PolygonEditor: Polygon drawing completed");

    // Clear any existing polygon
    this.clearPolygon();

    // Store the new polygon
    this.currentPolygon = polygon;

    // Disable drawing mode (only one polygon allowed)
    if (this.drawingManager) {
      this.drawingManager.setDrawingMode(null);
    }

    // Make the polygon editable
    polygon.setEditable(true);

    // Update the hidden form field
    this.updateGeoJsonField();

    // Listen for polygon edits
    this.addPolygonEditListeners(polygon);
  }

  private addPolygonEditListeners(polygon: google.maps.Polygon) {
    const path = polygon.getPath();

    google.maps.event.addListener(path, "set_at", () => {
      this.updateGeoJsonField();
    });

    google.maps.event.addListener(path, "insert_at", () => {
      this.updateGeoJsonField();
    });

    google.maps.event.addListener(path, "remove_at", () => {
      this.updateGeoJsonField();
    });
  }

  private loadInitialPolygon() {
    if (!this.initialPolygonValue || !this.googleMap) {
      return;
    }

    try {
      const polygon = this.geoJsonToPolygon(this.initialPolygonValue);
      if (polygon) {
        this.currentPolygon = polygon;
        polygon.setMap(this.googleMap);
        polygon.setEditable(true);

        // Auto-fit bounds to show the polygon
        this.fitBoundsToPolygon(polygon);

        // Listen for edits
        this.addPolygonEditListeners(polygon);
      }
    } catch (error) {
      console.error("Failed to load initial polygon:", error);
    }
  }

  private geoJsonToPolygon(geoJsonString: string): google.maps.Polygon | null {
    try {
      const geoJson = JSON.parse(geoJsonString);

      // Handle both Polygon and MultiPolygon types
      let coordinates: number[][][];
      if (geoJson.type === "MultiPolygon") {
        // Take the first polygon from the multipolygon
        coordinates = geoJson.coordinates[0];
      } else if (geoJson.type === "Polygon") {
        coordinates = geoJson.coordinates;
      } else {
        console.error("Unsupported GeoJSON type:", geoJson.type);
        return null;
      }

      // Convert GeoJSON coordinates to Google Maps LatLng
      // GeoJSON uses [lng, lat] so we reverse to [lat, lng]
      const paths = coordinates.map((ring) => {
        return ring.map((coord) => {
          return new google.maps.LatLng(coord[1], coord[0]);
        });
      });

      return new google.maps.Polygon({
        paths: paths,
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
        editable: true,
        draggable: false,
      });
    } catch (error) {
      console.error("Error parsing GeoJSON:", error);
      return null;
    }
  }

  private polygonToGeoJson(polygon: google.maps.Polygon): string {
    const path = polygon.getPath();
    const coordinates: number[][] = [];

    // Convert Google Maps LatLng to GeoJSON coordinates
    // Google Maps uses {lat, lng} but GeoJSON uses [lng, lat]
    for (let i = 0; i < path.getLength(); i++) {
      const latLng = path.getAt(i);
      coordinates.push([latLng.lng(), latLng.lat()]);
    }

    // Close the ring by adding the first coordinate at the end
    if (coordinates.length > 0) {
      const firstCoord = coordinates[0];
      coordinates.push([firstCoord[0], firstCoord[1]]);
    }

    // Wrap in MultiPolygon structure as required by PostGIS column
    const geoJson = {
      type: "MultiPolygon",
      coordinates: [[coordinates]],
    };

    return JSON.stringify(geoJson);
  }

  private updateGeoJsonField() {
    if (!this.currentPolygon) {
      this.geoJsonInputTarget.value = "";
      console.log("PolygonEditor: Cleared GeoJSON field");
      return;
    }

    const geoJson = this.polygonToGeoJson(this.currentPolygon);
    this.geoJsonInputTarget.value = geoJson;
    console.log(
      "PolygonEditor: Updated GeoJSON field with:",
      geoJson.substring(0, 100) + "...",
    );
  }

  private fitBoundsToPolygon(polygon: google.maps.Polygon) {
    if (!this.googleMap) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    const path = polygon.getPath();

    for (let i = 0; i < path.getLength(); i++) {
      bounds.extend(path.getAt(i));
    }

    this.googleMap.fitBounds(bounds);
  }

  public clearPolygon() {
    if (this.currentPolygon) {
      this.currentPolygon.setMap(null);
      this.currentPolygon = null;
      this.updateGeoJsonField();
    }

    // Re-enable drawing mode
    if (this.drawingManager) {
      this.drawingManager.setDrawingMode(
        google.maps.drawing.OverlayType.POLYGON,
      );
    }
  }

  private get mapCenter(): google.maps.LatLngLiteral {
    if (this.centerValue) {
      try {
        const center = JSON.parse(this.centerValue);
        return { lat: center.lat, lng: center.lng };
      } catch (error) {
        console.error("Error parsing center value:", error);
      }
    }

    // Default fallback center
    return { lat: 33.6265064, lng: -84.5319427 };
  }

  private cleanup() {
    if (this.currentPolygon) {
      this.currentPolygon.setMap(null);
      this.currentPolygon = null;
    }

    if (this.drawingManager) {
      this.drawingManager.setMap(null);
      this.drawingManager = null;
    }

    this.googleMap = null;
  }
}
